import { Component, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Observable, of } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { debounceTime, switchMap, map, startWith, finalize } from 'rxjs/operators';
import { AuthService } from '../../services';
import { ChangeDetectorRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

interface UserOption {
  id: string;
  email: string;
}

@Component({
  selector: 'educare-autocomplete-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './auto-complete-dialog.component.html',
  styleUrls: ['./auto-complete-dialog.component.scss']
})
export class AutocompleteDialogComponent implements OnInit {
  public readonly dialogRef = inject(MatDialogRef<AutocompleteDialogComponent>);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly data = inject<{ preSelected?: UserOption[]; audienceType?: string }>(MAT_DIALOG_DATA);

  myControl = new FormControl('');
  filteredOptions: Observable<UserOption[]> = of([]);
  selectedOptions: UserOption[] = [];
  showAutocomplete = false;
  loading = false;
  private emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  ngOnInit(): void {
    if (this.data?.preSelected?.length) {
      this.selectedOptions = [...this.data.preSelected];
    }

    this.filteredOptions = this.myControl.valueChanges.pipe(
      debounceTime(300),
      startWith(''),
      switchMap((value) => this.fetchUsers(value))
    );
  }

  fetchUsers(value: any): Observable<UserOption[]> {
    const query = value?.trim();
    this.showAutocomplete = !!query;
    // For EXTERNAL in CREATE mode: do not call API
    if (this.data.audienceType === 'EXTERNAL') {
      if (this.emailRegex.test(query)) {
        const alreadyAdded = this.selectedOptions.some((o) => o.email === query);
        return of(alreadyAdded ? [] : [{ id: '', email: query }]);
      }
      return of([]);
    }
    if (!query) return of([]);
    this.loading = true;

    return this.authService.get(`/user/lookup/users?email=${query}`).pipe(
      map((res: any) => res?.data?.users_list || []),
      finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      })
    );
  }

  displayFn(option: UserOption): string {
    return option?.email ?? '';
  }

  optionSelected(option: UserOption): void {
    if (!this.selectedOptions.find((o) => o.email === option.email)) {
      this.selectedOptions.push(option);
    }
    this.myControl.setValue('');
  }

  onManualInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.filteredOptions = this.fetchUsers(input);
  }

  remove(option: UserOption): void {
    this.selectedOptions = this.selectedOptions.filter((o) => {
      if (option?.id) {
        return o.id !== option.id;
      }
      return o?.email?.toLowerCase() !== option?.email?.toLowerCase();
    });
  }

  select(): void {
    this.dialogRef.close({
      selectedOptions: this.selectedOptions
    });
  }

  isAlreadySelected(option: UserOption): boolean {
    return this.selectedOptions.some((o) => {
      if (option.id) {
        return o.id === option.id;
      }
      return o.email.toLowerCase() === option.email.toLowerCase();
    });
  }
}
